/**
 * Listado de manifiestos de despacho.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowDownTrayIcon,
  EyeIcon,
  FunnelIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { Button } from '../../components/ui'
import DataTable from '../../components/ui/DataTable'
import { MANIFIESTOS_CARRIERS, carrierLabel } from '../../config/manifiestos/manifiestosCarriersConfig'
import { usePermissions } from '../../hooks/usePermissions'
import { listarManifiestos } from '../../services/manifiestos/manifiestosService'
import {
  MANIFIESTO_ESTADO_IMPRESO,
  MANIFIESTO_ESTADO_LISTO,
  type Manifiesto,
} from '../../types/manifiestos'
import { imprimirManifiestoPdf } from '../../utils/manifiestos/imprimirManifiesto'

const ESTADOS = [
  'Creado',
  'En verificación',
  'Listo para imprimir',
  'Impreso',
  'Cerrado',
]

const ESTADO_CLASS: Record<string, string> = {
  Creado: 'bg-gray-100 text-gray-600',
  'En verificación': 'bg-amber-100 text-amber-700',
  'Listo para imprimir': 'bg-blue-50 text-custom-blue',
  Impreso: 'bg-purple-100 text-purple-700',
  Cerrado: 'bg-green-100 text-green-700',
}

interface Fila {
  id: number
  consecutivo: string
  transportadora: string
  transportadoraValor: string
  fecha: string
  estado: string
  pedidos: string
  enManifiesto: string[]
  porAgregar: string[]
}

function formatFecha(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function toFila(item: Manifiesto): Fila {
  return {
    id: item.id,
    consecutivo: item.consecutivo || `MN-${String(item.id).padStart(5, '0')}`,
    transportadora: carrierLabel(item.transportadora) || item.transportadora || '',
    transportadoraValor: item.transportadora || '',
    fecha: formatFecha(item.fechaCreacion || ''),
    estado: item.estado,
    enManifiesto: (item.pedidos || [])
      .filter((linea) => String(linea.estado || 'Activo') === 'Activo')
      .map((linea) => String(linea.numero_pedido || '').trim())
      .filter(Boolean),
    porAgregar: (item.pedidosPorAgregar || [])
      .map((linea) => String(linea.numero_pedido || '').trim())
      .filter(Boolean),
    pedidos: [
      ...(item.pedidos || []).map((linea) => String(linea.numero_pedido || '').trim()),
      ...(item.pedidosPorAgregar || []).map((linea) => String(linea.numero_pedido || '').trim()),
    ].filter(Boolean).join(', '),
  }
}

const ManifiestosLista: React.FC = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission('manage_manifiestos')
  const [rows, setRows] = useState<Fila[]>([])
  const [loading, setLoading] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [transportadora, setTransportadora] = useState('')
  const [estado, setEstado] = useState('')
  const [workingId, setWorkingId] = useState<number | null>(null)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listarManifiestos({ transportadora, estado })
      setRows(data.map(toFila))
    } catch (error) {
      setRows([])
      toast.error(error instanceof Error ? error.message : 'No se pudieron listar los manifiestos', {
        position: 'top-center',
      })
    } finally {
      setLoading(false)
    }
  }, [transportadora, estado])

  useEffect(() => {
    void cargar()
  }, [cargar])

  const imprimir = async (fila: Fila) => {
    setWorkingId(fila.id)
    try {
      const ok = await imprimirManifiestoPdf(fila.id, fila.transportadoraValor)
      if (ok) toast.success('Manifiesto listo para imprimir', { position: 'top-center' })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No se pudo armar el PDF', {
        position: 'top-center',
      })
    } finally {
      setWorkingId(null)
    }
  }

  const columns = useMemo(
    () => [
      { header: 'CONSECUTIVO', accessor: 'consecutivo' as const, sortable: true },
      {
        header: 'PEDIDOS',
        accessor: 'pedidos' as const,
        cellOverflowVisible: true,
        cell: (row: Fila) => {
          if (row.enManifiesto.length === 0 && row.porAgregar.length === 0) {
            return <span className="text-gray-400">—</span>
          }
          return (
            <div className="flex flex-col gap-1.5 py-1">
              {row.enManifiesto.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {row.enManifiesto.map((numero) => (
                    <span key={`en-${numero}`} className="inline-flex px-2 py-0.5 rounded bg-blue-50 text-custom-blue text-xs font-medium">
                      {numero}
                    </span>
                  ))}
                </div>
              )}
              {row.porAgregar.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-[11px] text-custom-gray-dark">Por agregar</span>
                  {row.porAgregar.map((numero) => (
                    <span key={`ag-${numero}`} className="inline-flex px-2 py-0.5 rounded border border-dashed border-custom-blue text-custom-blue-2 text-xs">
                      {numero}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        },
      },
      { header: 'TRANSPORTADORA', accessor: 'transportadora' as const, sortable: true },
      { header: 'FECHA CREACIÓN', accessor: 'fecha' as const, sortable: true },
      {
        header: 'ESTADO',
        accessor: 'estado' as const,
        sortable: true,
        cell: (row: Fila) => (
          <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${ESTADO_CLASS[row.estado] || 'bg-gray-100 text-gray-600'}`}>
            {row.estado}
          </span>
        ),
      },
      {
        header: 'ACCIONES',
        accessor: 'id' as const,
        cell: (row: Fila) => {
          const puedeImprimir = row.estado === MANIFIESTO_ESTADO_LISTO || row.estado === MANIFIESTO_ESTADO_IMPRESO
          return (
            <div className="flex items-center gap-2 text-gray-500">
              <button type="button" aria-label={`Ver ${row.consecutivo}`} className="hover:text-blue-600" onClick={() => navigate(`/envios/manifiestos/${row.id}`)}>
                <EyeIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label={`Imprimir ${row.consecutivo}`}
                className={puedeImprimir ? 'hover:text-blue-600' : 'opacity-30 cursor-not-allowed'}
                disabled={!puedeImprimir || workingId === row.id}
                onClick={() => void imprimir(row)}
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            </div>
          )
        },
      },
    ],
    [navigate, workingId]
  )

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-custom-blue">Manifiestos de despacho</h2>
          <p className="text-sm text-gray-500 mt-1">Gestiona los manifiestos de entrega por transportadora</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" outline leftIcon={FunnelIcon} onClick={() => setShowFilters((value) => !value)} aria-label="Filtros">
            Filtros
          </Button>
          {canManage && (
            <Button variant="primary" leftIcon={PlusIcon} onClick={() => navigate('/envios/manifiestos/crear')}>
              Crear manifiesto
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <select className="border border-gray-300 rounded-md px-3 py-2" value={transportadora} aria-label="Filtrar transportadora" onChange={(event) => setTransportadora(event.target.value)}>
            <option value="">Todas las transportadoras</option>
            {MANIFIESTOS_CARRIERS.map((item) => (
              <option key={item.id} value={item.transportadora}>{item.label}</option>
            ))}
          </select>
          <select className="border border-gray-300 rounded-md px-3 py-2" value={estado} aria-label="Filtrar estado" onChange={(event) => setEstado(event.target.value)}>
            <option value="">Todos los estados</option>
            {ESTADOS.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>
      )}

      <DataTable data={rows} columns={columns} isLoading={loading} emptyMessage="No hay manifiestos" getRowId={(row) => row.id} />
    </div>
  )
}

export default ManifiestosLista
